import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const SuggestedConnections = () => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connectionRequests, setConnectionRequests] = useState(new Set());

  useEffect(() => {
    fetchSuggestions();
  }, []);

  const fetchSuggestions = async () => {
    try {
      setLoading(true);

      // Get current user
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session?.user) return;

      // Fetch potential connections (users not already connected)
      const { data, error } = await supabase?.from('user_profiles')?.select(`
          id,
          full_name,
          avatar_url,
          role,
          partners!inner (
            business_name,
            partner_type,
            artist_profession,
            is_verified,
            city,
            total_bookings
          )
        `)?.neq('id', session?.user?.id)?.limit(5);

      if (error) throw error;

      setSuggestions(data || []);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const sendConnectionRequest = async (userId) => {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session?.user) return;

      setConnectionRequests(prev => new Set([...prev, userId]));

      const { error } = await supabase?.from('professional_connections')?.insert({
          requester_id: session?.user?.id,
          recipient_id: userId,
          connection_message: 'Bonjour ! J\'aimerais me connecter avec vous sur Artis.io pour élargir mon réseau professionnel artistique.'
        });

      if (error) throw error;

      // Remove from suggestions after successful request
      setSuggestions(prev => prev?.filter(user => user?.id !== userId));
    } catch (error) {
      console.error('Error sending connection request:', error);
      setConnectionRequests(prev => {
        const newSet = new Set(prev);
        newSet?.delete(userId);
        return newSet;
      });
    }
  };

  const getPartnerTypeLabel = (partnerType) => {
    const labels = {
      'agent': 'Agent',
      'venue': 'Venue',
      'booker': 'Booker', 
      'independent': 'Indépendant'
    };
    return labels?.[partnerType] || partnerType;
  };

  const getProfessionLabel = (profession) => {
    const professions = {
      'musician': 'Musicien',
      'singer': 'Chanteur',
      'dj': 'DJ',
      'producer': 'Producteur',
      'model': 'Modèle',
      'actor': 'Acteur',
      'dancer': 'Danseur',
      'performer': 'Artiste'
    };
    return professions?.[profession] || profession;
  };

  if (loading) {
    return (
      <div className="bg-surface rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Connexions suggérées</h3>
        <div className="space-y-4">
          {[1, 2, 3]?.map((i) => (
            <div key={i} className="animate-pulse">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-muted rounded-full"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-muted rounded w-3/4"></div>
                  <div className="h-3 bg-muted rounded w-1/2"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">Connexions suggérées</h3>
        <Link
          to="/connection-recommendations"
          className="text-accent hover:text-accent/80 text-sm font-medium transition-colors"
        >
          Voir tout
        </Link>
      </div>
      {suggestions?.length > 0 ? (
        <div className="space-y-4">
          {suggestions?.map((user) => {
            const partner = user?.partners?.[0];
            const isRequestSent = connectionRequests?.has(user?.id);
            
            return (
              <div key={user?.id} className="flex items-start space-x-3">
                {/* Avatar */}
                <Link to={`/enhanced-artist-profile`} className="flex-shrink-0">
                  {user?.avatar_url ? (
                    <img
                      src={user?.avatar_url}
                      alt={partner?.business_name || user?.full_name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                      <span className="text-primary-foreground font-semibold text-sm">
                        {(partner?.business_name || user?.full_name)?.charAt(0)?.toUpperCase() || 'U'}
                      </span>
                    </div>
                  )}
                </Link>
                {/* User Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-1 mb-1">
                    <Link 
                      to={`/enhanced-artist-profile`}
                      className="font-semibold text-foreground hover:text-accent transition-colors text-sm truncate"
                    >
                      {partner?.business_name || user?.full_name}
                    </Link>
                    {partner?.is_verified && (
                      <div className="w-3 h-3 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <Icon name="Check" size={8} color="white" />
                      </div>
                    )}
                  </div>
                  
                  <div className="text-xs text-muted-foreground mb-2">
                    <div className="flex items-center space-x-2">
                      <span>{getPartnerTypeLabel(partner?.partner_type)}</span>
                      {partner?.artist_profession && (
                        <>
                          <span>•</span>
                          <span>{getProfessionLabel(partner?.artist_profession)}</span>
                        </>
                      )}
                    </div>
                    {partner?.city && (
                      <div className="flex items-center space-x-1 mt-1">
                        <Icon name="MapPin" size={10} />
                        <span>{partner?.city}</span>
                      </div>
                    )}
                  </div>

                  {/* Connection Reason */}
                  <div className="text-xs text-muted-foreground mb-3">
                    {partner?.partner_type === 'agent' && '🎯 Agent dans votre domaine'}
                    {partner?.partner_type === 'venue' && '🏢 Venue dans votre région'}
                    {partner?.partner_type === 'independent' && partner?.artist_profession && 
                      `🎵 ${getProfessionLabel(partner?.artist_profession)} comme vous`}
                    {partner?.total_bookings > 0 && ` • ${partner?.total_bookings} réservations`}
                  </div>

                  {/* Connect Button */}
                  <Button
                    size="xs"
                    variant={isRequestSent ? "outline" : "default"}
                    onClick={() => !isRequestSent && sendConnectionRequest(user?.id)}
                    disabled={isRequestSent}
                    iconName={isRequestSent ? "Check" : "UserPlus"}
                    className="w-full"
                  >
                    {isRequestSent ? 'Demande envoyée' : 'Se connecter'}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-8 text-muted-foreground">
          <Icon name="Users" size={24} className="mx-auto mb-2 opacity-50" />
          <p className="text-sm">Aucune suggestion pour le moment</p>
        </div>
      )}
      <div className="mt-6 pt-4 border-t border-border">
        <Link
          to="/connection-recommendations"
          className="block w-full"
        >
          <Button variant="outline" size="sm" className="w-full" iconName="ArrowRight">
            Voir toutes les recommandations
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default SuggestedConnections;